import classNames from 'classnames';
import { useState, memo, useEffect, useMemo, useRef } from 'react';

import ClearSearchButton from '../../../components/ClearSearchButton';
import type { RBACGroup } from '../../../types';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav';

type Props = {
  items: RBACGroup[];
  selectedId?: string;
  onSelect: (value: RBACGroup) => void;
  // Invoked by the in-input X button after the text is cleared — lets the caller
  // also drop the current selection (e.g. navigate back to the unselected route).
  onClear?: () => void;
};

function ACLAutocomplete({ items, selectedId, onSelect, onClear }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [groupList, setGroupList] = useState<RBACGroup[]>(items);

  // The daisyUI .dropdown reveals its content purely via CSS :focus-within, so we can't
  // both keep the input focused after a pick AND have the list stay closed unless we gate
  // whether the content is rendered at all. isOpen is that gate: it starts true (the list
  // is rendered but stays CSS-hidden until focus, as before) and is only force-closed after
  // a pick, staying closed until the next focus/keystroke.
  const [isOpen, setIsOpen] = useState(true);
  // Picking refocuses the input; that refocus fires onFocus, which would otherwise re-open
  // the list we just closed. This one-shot flag swallows exactly that programmatic refocus.
  const suppressOpenRef = useRef(false);

  // Fill the input with the selected ACL's name when the selection changes
  useEffect(() => {
    const selected = items.find((item) => item.id === selectedId);
    if (selected) {
      setValue(selected.displayName);
    } else {
      setValue('');
    }
  }, [selectedId, items]);

  // Filter list based on entered value
  useEffect(() => {
    setGroupList(
      items.filter((item) => item.displayName?.toLowerCase().includes(value.toLowerCase()))
    );
  }, [value, items]);

  // Memoized so the keyboard-nav hook only resets when the results actually change.
  const sortedList = useMemo(
    () => [...groupList].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [groupList]
  );

  const handleClick = (item: RBACGroup) => {
    onSelect(item);
    // Close the list but keep the input focused.
    suppressOpenRef.current = true;
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const { activeIndex, setActiveIndex, handleKeyDown, listRef } = useListKeyboardNav(
    sortedList,
    handleClick,
    () => setIsOpen(false),
    () => setIsOpen(true)
  );

  // Select-all on focus is handy for replacing the prefilled ACL name when tabbing in,
  // but it hijacks click-to-position-caret. So only select-all when focus did NOT come
  // from a mouse press; a click then positions the caret normally.
  const focusedByMouse = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    focusedByMouse.current = e.currentTarget !== document.activeElement;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Swallow the programmatic refocus fired by a pick: don't re-open or re-select.
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false;
      return;
    }
    if (!focusedByMouse.current) {
      e.target.select();
    }
    setIsOpen(true);
  };

  const handleBlur = () => {
    focusedByMouse.current = false;
  };

  return (
    <div className="dropdown w-72">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="input input-bordered w-full pr-9 focus:outline-none"
          placeholder="Search for an ACL group"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsOpen(true);
          }}
          tabIndex={0}
          onMouseDown={handleMouseDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <ClearSearchButton
            onClick={() => {
              setValue('');
              onClear?.();
              inputRef.current?.focus();
            }}
          />
        )}
      </div>
      {!isOpen || sortedList.length === 0 ? (
        <div></div>
      ) : (
        <div className="w-72 dropdown-content border border-base-200 bg-base-100 top-14 max-h-[50rem] overflow-auto flex-col rounded-md">
          <ul className="menu menu-compact" ref={listRef}>
            {sortedList.map((item, index) => (
              <li
                key={item.id}
                className={classNames('border-b last:border-b-0 border-b-base-200 w-full', {
                  'bg-primary text-primary-content': index === activeIndex,
                })}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => handleClick(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className="flex flex-col items-start gap-0 w-full text-left"
                >
                  <div className="truncate">{item.displayName}</div>
                  {'description' in item && item.description && (
                    <div className="text-xs w-full truncate" title={item.description}>
                      {item.description}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default memo(ACLAutocomplete);
